import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';

const REGIME_STYLES = {
  panic: { color: '#c62828', bg: '#ffebee', labelKey: 'panic' },
  cautious: { color: '#e65100', bg: '#fff3e0', labelKey: 'cautious' },
  stable: { color: '#1565c0', bg: '#e3f2fd', labelKey: 'stable' },
  bullish: { color: '#2e7d32', bg: '#e8f5e9', labelKey: 'bullish' },
  euphoric: { color: '#ad1457', bg: '#fce4ec', labelKey: 'euphoric' },
};

const ACTION_COLORS = {
  BUY_WEAKNESS: '#2e7d32',
  ACCUMULATE_SIGNAL: '#2e7d32',
  MUA_THAM_DO: '#2e7d32',
  TANG_TY_TRONG: '#2e7d32',
  SELL_STRENGTH: '#c62828',
  TAKE_PROFIT: '#c62828',
  GIAM_TY_TRONG: '#c62828',
  EXTREME_BIAS: '#e65100',
  MILD_BIAS: '#e65100',
  NEUTRAL: '#475569',
  TRUNG_TINH: '#475569',
  QUAN_SAT: '#475569',
};

const formatActionLabel = (code, t) => {
  if (!code) return '—';
  return t(`sentiment.behavior.actions.${code}`, {
    defaultValue: code.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()),
  });
};

const formatActionHint = (code, panic, fomo, t) => {
  if (!code) return '';

  if (code === 'EXTREME_BIAS' || code === 'MILD_BIAS') {
    if (panic > fomo) {
      return t('sentiment.behavior.actions.bias_panic_hint', {
        defaultValue: 'Fear outweighs excitement — not yet a clear buy signal',
      });
    }
    if (fomo > panic) {
      return t('sentiment.behavior.actions.bias_fomo_hint', {
        defaultValue: 'Excitement outweighs fear — not yet a clear sell signal',
      });
    }
  }

  return t(`sentiment.behavior.actions.${code}_hint`, { defaultValue: '' });
};

const StatColumn = ({ label, value, color }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 0.15,
      minWidth: 0,
    }}
  >
    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
    <Typography sx={{ color, fontWeight: 800, fontSize: '0.9rem', lineHeight: 1.1 }}>
      {value}
    </Typography>
  </Box>
);

StatColumn.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

const BehaviorOverview = ({ signal, insights, t }) => {
  if (!signal && !insights) return null;

  const panic_val = signal?.panic_score ?? 0;
  const fomo_val = signal?.fomo_score ?? 0;
  const confidence_val = signal?.signal_confidence ?? 0;
  const regime = insights?.current_regime || 'stable';
  const regimeStyle = REGIME_STYLES[regime] || REGIME_STYLES.stable;
  const actionCode = signal?.action_level || signal?.recommended_action || insights?.recommended_action;
  const actionLabel = formatActionLabel(actionCode, t);
  const actionHint = formatActionHint(actionCode, panic_val, fomo_val, t);
  const regimeLabel = t(`sentiment.behavior.regime.${regimeStyle.labelKey}`, regime);
  const takeaway = actionHint || t(`sentiment.behavior.regime.${regimeStyle.labelKey}_desc`, '');

  const stats = [
    {
      key: 'panic',
      label: t('sentiment.behavior.signal.panic', 'Panic Score'),
      value: `${(panic_val * 100).toFixed(1)}%`,
      color: panic_val >= 0.65 ? '#e65100' : '#334155',
    },
    {
      key: 'fomo',
      label: t('sentiment.behavior.signal.fomo', 'FOMO Score'),
      value: `${(fomo_val * 100).toFixed(1)}%`,
      color: fomo_val >= 0.65 ? '#00838f' : '#334155',
    },
    {
      key: 'action',
      label: t('sentiment.behavior.signal.recommendation', 'Guidance'),
      value: actionLabel,
      color: ACTION_COLORS[actionCode] || '#0f172a',
    },
    {
      key: 'confidence',
      label: t('sentiment.behavior.signal.confidence', 'Confidence'),
      value: `${(confidence_val * 100).toFixed(0)}%`,
      color: confidence_val >= 0.6 ? '#2563eb' : '#64748b',
    },
  ];

  return (
    <Box
      sx={{
        mb: 2.5,
        px: 1.75,
        py: 1.25,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'flex-start' },
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary', lineHeight: 1.3 }}>
              {t('sentiment.behavior.signal.title', 'Market Behavior Signal')}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: '0.68rem',
                fontWeight: 700,
                color: regimeStyle.color,
                letterSpacing: '0.03em',
                lineHeight: 1,
              }}
            >
              {regimeLabel}
            </Typography>
          </Box>
          {takeaway && (
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.35, lineHeight: 1.45, maxWidth: 520 }}>
              {takeaway}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: { xs: 2, sm: 2.5 },
            flexWrap: 'wrap',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            flex: '0 1 auto',
          }}
        >
          {stats.map((item) => (
            <StatColumn
              key={item.key}
              label={item.label}
              value={item.value}
              color={item.color}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

BehaviorOverview.propTypes = {
  signal: PropTypes.shape({
    trade_date: PropTypes.string,
    panic_score: PropTypes.number,
    fomo_score: PropTypes.number,
    action_level: PropTypes.string,
    signal_confidence: PropTypes.number,
    recommended_action: PropTypes.string,
  }),
  insights: PropTypes.shape({
    current_regime: PropTypes.string,
    market_interpretation: PropTypes.string,
    recommended_action: PropTypes.string,
  }),
  t: PropTypes.func.isRequired,
};

export default BehaviorOverview;
